import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import api from "../api/api";
import Header from "./Header";

export default function OrderSummaryTabs() {
  const [activeTab, setActiveTab] = useState(1); // 1 = Confirmed, 2 = Unconfirmed
  const [confirmedOrders, setConfirmedOrders] = useState([]);
  const [unconfirmedOrders, setUnconfirmedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const user = sessionStorage.getItem("username") || "UnknownUser";

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);

        // ✅ Fetch confirmed orders
        const confirmedResp = await api.GetOrderDetailsRev(user, 1);
        const confirmedData = confirmedResp?.GetOrderDetailsRevjs?.Table || [];
        setConfirmedOrders(confirmedData);

        // ✅ Fetch unconfirmed orders
        const unconfirmedResp = await api.GetOrderDetailsRev(user, 0);
        const unconfirmedData = unconfirmedResp?.GetOrderDetailsRevjs?.Table || [];
        setUnconfirmedOrders(unconfirmedData);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [user]);

  const handleBack = () => window.history.back();

  const handleLogout = () => {
    sessionStorage.removeItem("isLoggedIn");
    sessionStorage.removeItem("username");
    sessionStorage.removeItem("lastVisited");
    window.location.href = "/";
  };

  const exportToExcel = (orders, filename) => {
    const exportData = orders.map((order, index) => ({
      SlNo: index + 1,
      Customer: order.Vname,
      Area: order.Area,
      Division: order.Division,
      "Order Date": order.OrderDate
        ? new Date(order.OrderDate).toLocaleDateString("en-GB")
        : "-",
      "Delivery Date": order.DeliveryDate
        ? new Date(order.DeliveryDate).toLocaleDateString("en-GB")
        : "-",
      Meal: order.FoodType,
      Item: order.Item_desc,
      Qty: order.Qty,
      Amount: order.Amount,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");
    XLSX.writeFile(workbook, filename);
  };

  const getFilteredOrders = (orders) => {
    const query = searchQuery.toLowerCase();
    return orders.filter(
      (order) =>
        order.Vname?.toLowerCase().includes(query) ||
        order.Area?.toLowerCase().includes(query) ||
        order.Division?.toLowerCase().includes(query) ||
       (order.DeliveryDate &&
  new Date(order.DeliveryDate)
    .toLocaleDateString("en-GB")
    .toLowerCase()
    .includes(query))
    );
  };

  const renderTable = (orders) => {
    const filteredOrders = getFilteredOrders(orders);

    return (
      <div className="overflow-x-auto border border-gray-200 rounded-xl shadow-inner">
        <table className="min-w-full text-sm text-gray-700">
          <thead className="sticky top-0 bg-gradient-to-r from-orange-500 to-orange-500 text-white">
            <tr>
              {[
                "Sl No",
                "Customer",
                "Area",
                "Division",
                "Order Date",
                "Delivery Date",
                "Meal",
                "Item",
                "Qty",
                "Amount",
              ].map((header, i) => (
                <th
                  key={i}
                  className={`px-3 sm:px-4 py-3 ${
                    i >= 8 ? "text-right" : "text-left"
                  }`}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length > 0 ? (
              filteredOrders.map((order, index) => (
                <tr
                  key={index}
                  className={`transition hover:bg-orange-50 ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                  }`}
                >
                  <td className="px-3 sm:px-4 py-3">{index + 1}</td>
                  <td className="px-3 sm:px-4 py-3 font-semibold text-gray-900">
                    {order.Vname}
                  </td>
                  <td className="px-3 sm:px-4 py-3">{order.Area}</td>
                  <td className="px-3 sm:px-4 py-3">{order.Division}</td>
                  <td className="px-3 sm:px-4 py-3">
  {order.OrderDate
    ? new Date(order.OrderDate).toLocaleDateString("en-GB")
    : "-"}
</td>

<td className="px-3 sm:px-4 py-3">
  {order.DeliveryDate
    ? new Date(order.DeliveryDate).toLocaleDateString("en-GB")
    : "-"}
</td>

                  <td className="px-3 sm:px-4 py-3 capitalize">
                    {order.FoodType}
                  </td>
                  <td className="px-3 sm:px-4 py-3">{order.Item_desc}</td>
                  <td className="px-3 sm:px-4 py-3 text-right">{order.Qty}</td>
                  <td className="px-3 sm:px-4 py-3 text-right font-semibold text-gray-900">
                    ₹{order.Amount}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="py-6 text-center text-gray-500">
                  No matching orders for <strong>{user}</strong>.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="mt-16 text-center text-gray-600 animate-pulse">
        Loading orders for <strong>{user}</strong>...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onLogout={handleLogout} />
      <main className="p-6 md:ml-64">
        <div className="p-4 sm:p-6 mx-auto mt-3 bg-white shadow-2xl rounded-2xl max-w-8xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b pb-3 mb-4">
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-1 px-3 py-1 text-gray-800 font-semibold 
             bg-gray-200 hover:bg-gray-300 rounded-full shadow transition 
             text-sm sm:text-base w-auto self-start"
        >
          ← Back
        </button>

        <h1 className="text-2xl sm:text-3xl font-bold text-orange-600 text-center flex-1">
          Order Details
        </h1>

        <button
          onClick={() =>
            exportToExcel(
              activeTab === 1 ? confirmedOrders : unconfirmedOrders,
              activeTab === 1
                ? "ConfirmedOrders.xlsx"
                : "UnconfirmedOrders.xlsx"
            )
          }
          className="px-2 py-1 font-semibold inline-flex w-auto self-start text-white bg-blue-500 hover:bg-blue-700 rounded-full shadow transition"
        >
          📄 Export Excel
        </button>
      </div>

      {/* Tabs */}
      <div className="flex mb-4 border-b gap-3 border-gray-300">
        <button
          className={`px-4 py-2 -mb-px font-semibold rounded-t-lg ${
            activeTab === 1
              ? "bg-orange-500 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
          onClick={() => setActiveTab(1)}
        >
          Confirmed Orders
        </button>
        <button
          className={`px-4 py-2 -mb-px font-semibold rounded-t-lg ${
            activeTab === 2
              ? "bg-orange-500 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
          onClick={() => setActiveTab(2)}
        >
          Unconfirmed Orders
        </button>
      </div>

      {/* Search */}
      <div className="mb-4 flex justify-end">
        <input
          type="text"
          placeholder="🔍Search Customer, Area, Division, Delivery Date..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full sm:w-1/3 px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
        />
      </div>

      {/* Table */}
      {activeTab === 1
        ? renderTable(confirmedOrders)
        : renderTable(unconfirmedOrders)}
        </div>
      </main>
    </div>
  );
}

